import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PdfIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { Proposta, PropostaFormData } from '../../types/proposta';
import { PropostaService } from '../../services/propostaService';
import PropostaForm from '../../components/forms/PropostaForm';
import PropostaViewDialog from '../../components/propostas/PropostaViewDialog';
import { formatarCPFCNPJ, formatarData, formatarValor } from '../../utils/formatToView';

const PropostasPage: React.FC = () => {
  const [propostas, setPropostas] = useState<Proposta[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const [openForm, setOpenForm] = useState(false);
  const [editingProposta, setEditingProposta] = useState<Proposta | null>(null);

  const [viewProposta, setViewProposta] = useState<Proposta | null>(null);
  const [openView, setOpenView] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [propostaToDelete, setPropostaToDelete] = useState<Proposta | null>(null);
  const [exportingId, setExportingId] = useState<number | null>(null);

  const fetchPropostas = useCallback(async () => {
    try {
      setLoading(true);
      const response = await PropostaService.listar(page + 1, rowsPerPage, searchTerm);
      if (response?.data && response?.pagination) {
        setPropostas(response.data);
        setTotal(response.pagination.total);
      } else {
        setPropostas([]);
        setTotal(0);
      }
    } catch {
      toast.error('Erro ao carregar propostas');
      setPropostas([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchPropostas();
  }, [fetchPropostas]);

  const handleSearch = () => {
    setPage(0);
    setSearchTerm(searchInput);
  };

  const handleOpenForm = async (proposta?: Proposta) => {
    if (proposta) {
      try {
        const completa = await PropostaService.buscarPorId(proposta.id);
        setEditingProposta(completa);
      } catch {
        toast.error('Erro ao carregar proposta');
        return;
      }
    } else {
      setEditingProposta(null);
    }
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setEditingProposta(null);
  };

  const handleSubmitForm = async (data: PropostaFormData) => {
    if (editingProposta) {
      await PropostaService.atualizar(editingProposta.id, data);
      toast.success('Proposta atualizada com sucesso!');
    } else {
      await PropostaService.criar(data);
      toast.success('Proposta cadastrada com sucesso!');
    }
    fetchPropostas();
  };

  const handleView = async (proposta: Proposta) => {
    try {
      const completa = await PropostaService.buscarPorId(proposta.id);
      setViewProposta(completa);
      setOpenView(true);
    } catch {
      toast.error('Erro ao carregar proposta');
    }
  };

  const handleExportPdf = async (id: number) => {
    try {
      setExportingId(id);
      await PropostaService.exportarPdf(id);
    } finally {
      setExportingId(null);
    }
  };

  const handleDeleteClick = (proposta: Proposta) => {
    setPropostaToDelete(proposta);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!propostaToDelete) return;
    try {
      await PropostaService.excluir(propostaToDelete.id);
      toast.success('Proposta excluída com sucesso!');
      setDeleteDialogOpen(false);
      setPropostaToDelete(null);
      fetchPropostas();
    } catch {
      toast.error('Erro ao excluir proposta');
    }
  };

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <DescriptionIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>Propostas</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenForm()}>
          Nova Proposta
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={1} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Buscar por cliente, CPF/CNPJ ou contato..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              ),
            }}
          />
          <Button variant="outlined" onClick={handleSearch}>Buscar</Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Cliente</TableCell>
                  <TableCell>CPF/CNPJ</TableCell>
                  <TableCell>Data</TableCell>
                  <TableCell>Vencimento</TableCell>
                  <TableCell align="right">Valor Total</TableCell>
                  <TableCell>Criado por</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {propostas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nenhuma proposta encontrada
                    </TableCell>
                  </TableRow>
                ) : (
                  propostas.map((proposta) => (
                    <TableRow key={proposta.id} hover>
                      <TableCell>{proposta.id}</TableCell>
                      <TableCell>{proposta.nome_cliente}</TableCell>
                      <TableCell>{proposta.cpf_cnpj ? formatarCPFCNPJ(proposta.cpf_cnpj) : '-'}</TableCell>
                      <TableCell>{formatarData(proposta.data_proposta)}</TableCell>
                      <TableCell>{formatarData(proposta.data_vencimento)}</TableCell>
                      <TableCell align="right">{formatarValor(Number(proposta.valor_total))}</TableCell>
                      <TableCell>{proposta.usuario?.nome || '-'}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="Visualizar">
                          <IconButton size="small" color="info" onClick={() => handleView(proposta)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" color="primary" onClick={() => handleOpenForm(proposta)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Exportar PDF">
                          <IconButton size="small" color="secondary"
                            onClick={() => handleExportPdf(proposta.id)}
                            disabled={exportingId === proposta.id}>
                            {exportingId === proposta.id
                              ? <CircularProgress size={18} />
                              : <PdfIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" color="error" onClick={() => handleDeleteClick(proposta)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Por página:"
            />
          </>
        )}
      </TableContainer>

      <PropostaForm
        open={openForm}
        onClose={handleCloseForm}
        proposta={editingProposta}
        onSubmit={handleSubmitForm}
      />

      <PropostaViewDialog
        open={openView}
        onClose={() => { setOpenView(false); setViewProposta(null); }}
        proposta={viewProposta}
        onExportPdf={viewProposta ? () => handleExportPdf(viewProposta.id) : undefined}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Deseja realmente excluir a proposta <strong>#{propostaToDelete?.id}</strong> de{' '}
            <strong>{propostaToDelete?.nome_cliente}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Excluir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropostasPage;
